import { updateMaterialArg, UpdateMaterialByIdPayload } from './materials.types'
import { SubjectShort } from 'src/@types/modules/subjects/subjects.types'
import { provideTags } from 'src/api/utils/functions/provide-tags';
import { baseApi, QueryPayload } from 'src/api';

function appendFiles(
    formData: FormData,
    key: string,
    files: File[],
): void {
    if (!files) return
    
    if (!files.length) return
    
    for (const file of files) {
        formData.append(key, file, file.name)
    }
}

export type MaterialCreatePayload = {
    index: number;
    name: string;
    materialLink?: string;
    materialFiles: File[];
    homeworkFiles: File[];
    practiceFiles: File[];
    presentationFiles: File[];
}

const getCreateFormData = (payload: MaterialCreatePayload): FormData => {
    const formData: FormData = new FormData()
    
    formData.append('name', payload.name)
    formData.append('index', payload.index.toString())
    
    if (payload.materialLink)
        formData.append('materialLink', payload.materialLink)
    
    appendFiles(formData, 'materialFiles', payload.materialFiles)
    appendFiles(formData, 'practiceFiles', payload.practiceFiles)
    appendFiles(formData, 'homeworkFiles', payload.homeworkFiles)
    appendFiles(formData, 'presentationFiles', payload.presentationFiles)
    
    return formData
}

export type Material = {
    id: number;
    subject: SubjectShort;
    
    index: number;
    name: string;
    
    sourceLink: string | null;
    
    meta: {
        hasLesson: boolean;
        hasHomework: boolean;
        hasPractice: boolean;
        hasPresentation: boolean;
    }
    
    createdAt: number;
}

export const formatMaterialName = (payload: Pick<Material, 'name' | 'index'>): string => {
    const { name, index } = payload
    
    if (!index) return name
    if (name.match(/^Урок.*$/)) return name
    
    return `Урок ${index}. ${name}`
}

export const isMaterialLegacy = (payload: Pick<Material, 'index'>): boolean => {
    return !payload.index
}

const api = baseApi.injectEndpoints({
    endpoints: builder => ({
        getMaterial: builder.query<
            Material,
            { params: { materialId: number } }
        >({
            providesTags: (material: Material | undefined, _err, { params }) => [
                { type: 'Materials', id: params.materialId },
                ...(material ? [
                    { type: 'Subjects' as const, id: material.subject.id },
                ] : []),
            ],
            query: ({ params }) => {
                return {
                    url: `/materials/${params.materialId}`,
                    method: 'GET',
                }
            },
        }),
        
        getMaterials: builder.query<Material[], { query: { subjectId?: number } }>({
            providesTags: (res) => provideTags('Materials', res),
            query: ({ query }): QueryPayload => {
                return {
                    url: 'materials',
                    method: 'GET',
                    params: query,
                }
            },
        }),
        
        createMaterial: builder.mutation<void, { body: MaterialCreatePayload, params: { subjectId: number } }>({
            invalidatesTags: (_res, _err, { params }) => [
                { type: 'Materials', id: 'LIST' },
                { type: 'Subjects', id: params.subjectId },
            ],
            query: ({ body, params }): QueryPayload => {
                return {
                    url: `/subjects/${params.subjectId}/materials`,
                    method: 'POST',
                    data: getCreateFormData(body),
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            },
        }),
        
        updateMaterial: builder.mutation<void, UpdateMaterialByIdPayload>({
            invalidatesTags: (_res, _err, { params }) => [
                { type: 'Materials', id: 'LIST' },
                { type: 'Materials', id: params.materialId },
                { type: 'Subjects', id: params.subjectId },
            ],
            query: ({ params, body }: UpdateMaterialByIdPayload): QueryPayload => ({
                url: `/subjects/${params.subjectId}/materials/${params.materialId}`,
                method: 'PATCH',
                data: body,
            }),
        }),
        
        // TODO: Почему это здесь?
        changeDownloadStatus: builder.mutation<void, updateMaterialArg>({
            invalidatesTags: ['Materials'],
            query: arg => ({
                url: 'practice/changeDownloadStatus',
                method: 'post',
                data: arg,
                errorMessage: 'Не удалось поменять статус!',
            }),
        }),
        
        deleteMaterial: builder.mutation<void, { params: { materialId } }>({
            invalidatesTags: (_res, _err, { params }) => [
                { type: 'Materials', id: 'LIST' },
                { type: 'Materials', id: params.materialId },
            ],
            query: ({ params }): QueryPayload => ({
                url: `/materials/${params.materialId}`,
                method: 'DELETE',
            }),
        }),
    }),
})

export const {
    useGetMaterialQuery,
    useUpdateMaterialMutation,
    useChangeDownloadStatusMutation,
    useCreateMaterialMutation,
    useDeleteMaterialMutation,
    useGetMaterialsQuery,
} = api
